/**
 * TanStack Query Hooks for Folder Mutations
 *
 * Part of the Data Workspace OS feature set.
 * Provides folder write operations with:
 * - Optimistic updates for instant UI feedback
 * - Automatic rollback on errors
 * - Query invalidation on success
 *
 * @module features/organization/hooks/useFolderMutations
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  FolderCreateRequest,
  FolderUpdateRequest,
  FolderTreeNode,
  FolderWithCounts,
} from '@loader/shared-types';

import {
  addFolderToTree,
  updateFolderInTree,
  removeFolderFromTree,
  moveFolderInTree,
} from '../utils';

import { useRpc, extractErrorMessage } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new folder
 *
 * Uses optimistic updates for instant UI feedback.
 * Automatically invalidates folder queries on success.
 */
export function useCreateFolderMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: FolderCreateRequest) => {
      const res = await rpc.api.private.organization.folders.$post({
        json: data,
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to create folder'));
      }

      return res.json();
    },
    onMutate: async (newFolder: FolderCreateRequest) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.organization.folders.tree,
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.organization.folders.list(),
      });

      // Snapshot previous values for rollback
      const previousTree = queryClient.getQueryData<FolderTreeNode[]>(
        queryKeys.organization.folders.tree
      );
      const previousList = queryClient.getQueryData<FolderWithCounts[]>(
        queryKeys.organization.folders.list()
      );

      // Create optimistic folder with temporary ID
      const tempId = `temp-${Date.now()}`;
      const optimisticFolder: FolderWithCounts = {
        id: tempId,
        name: newFolder.name,
        color: newFolder.color || null,
        parent_folder_id: newFolder.parent_folder_id || null,
        workspace_id: '',
        created_by: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        deleted_at: null,
        table_count: 0,
        linked_table_count: 0,
        subfolder_count: 0,
      };

      const optimisticTreeNode: FolderTreeNode = {
        ...optimisticFolder,
        children: [],
      };

      // Optimistically update the tree
      if (previousTree) {
        queryClient.setQueryData<FolderTreeNode[]>(
          queryKeys.organization.folders.tree,
          addFolderToTree(previousTree, optimisticTreeNode, newFolder.parent_folder_id || null)
        );
      }

      // Optimistically update the list
      if (previousList) {
        queryClient.setQueryData<FolderWithCounts[]>(
          queryKeys.organization.folders.list(),
          [...previousList, optimisticFolder]
        );
      }

      return { previousTree, previousList };
    },
    onError: (_err, _newFolder, context) => {
      // Rollback on error
      if (context?.previousTree) {
        queryClient.setQueryData(
          queryKeys.organization.folders.tree,
          context.previousTree
        );
      }
      if (context?.previousList) {
        queryClient.setQueryData(
          queryKeys.organization.folders.list(),
          context.previousList
        );
      }
    },
    onSettled: () => {
      // Always refetch after mutation settles
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.folders.all,
      });
    },
  });
}

/**
 * Update an existing folder
 *
 * Supports partial updates (name, color, parent).
 * Uses optimistic updates for instant UI feedback.
 */
export function useUpdateFolderMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FolderUpdateRequest }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.folders[':id'].$patch({
        param: { id },
        json: data,
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to update folder'));
      }

      return res.json();
    },
    onMutate: async ({ id, data }: { id: string; data: FolderUpdateRequest }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.organization.folders.tree,
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.organization.folders.list(),
      });

      // Snapshot previous values for rollback
      const previousTree = queryClient.getQueryData<FolderTreeNode[]>(
        queryKeys.organization.folders.tree
      );
      const previousList = queryClient.getQueryData<FolderWithCounts[]>(
        queryKeys.organization.folders.list()
      );

      // Check if this is a move operation (parent_folder_id changed)
      const isMove = data.parent_folder_id !== undefined;

      // Optimistically update the tree
      if (previousTree) {
        let newTree: FolderTreeNode[];
        if (isMove) {
          // Move folder to new parent
          newTree = moveFolderInTree(previousTree, id, data.parent_folder_id ?? null);
        } else {
          // Just update properties
          newTree = updateFolderInTree(previousTree, id, data);
        }
        queryClient.setQueryData<FolderTreeNode[]>(
          queryKeys.organization.folders.tree,
          newTree
        );
      }

      // Optimistically update the list
      if (previousList) {
        queryClient.setQueryData<FolderWithCounts[]>(
          queryKeys.organization.folders.list(),
          previousList.map((folder) =>
            folder.id === id
              ? {
                  ...folder,
                  ...(data.name !== undefined && { name: data.name }),
                  ...(data.color !== undefined && { color: data.color }),
                  ...(data.parent_folder_id !== undefined && { parent_folder_id: data.parent_folder_id }),
                }
              : folder
          )
        );
      }

      return { previousTree, previousList };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousTree) {
        queryClient.setQueryData(
          queryKeys.organization.folders.tree,
          context.previousTree
        );
      }
      if (context?.previousList) {
        queryClient.setQueryData(
          queryKeys.organization.folders.list(),
          context.previousList
        );
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch after mutation settles
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.folders.detail(variables.id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.folders.all,
      });
    },
  });
}

/**
 * Delete a folder (soft delete to trash)
 *
 * Moves folder and its contents to trash.
 * Uses optimistic updates for instant UI feedback.
 */
export function useDeleteFolderMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.folders[':id'].$delete({
        param: { id },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to delete folder'));
      }

      return res.json();
    },
    onMutate: async (deletedId: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.organization.folders.tree,
      });
      await queryClient.cancelQueries({
        queryKey: queryKeys.organization.folders.list(),
      });

      // Snapshot previous values for rollback
      const previousTree = queryClient.getQueryData<FolderTreeNode[]>(
        queryKeys.organization.folders.tree
      );
      const previousList = queryClient.getQueryData<FolderWithCounts[]>(
        queryKeys.organization.folders.list()
      );

      // Optimistically update the tree
      if (previousTree) {
        queryClient.setQueryData<FolderTreeNode[]>(
          queryKeys.organization.folders.tree,
          removeFolderFromTree(previousTree, deletedId)
        );
      }

      // Optimistically update the list
      if (previousList) {
        queryClient.setQueryData<FolderWithCounts[]>(
          queryKeys.organization.folders.list(),
          previousList.filter((folder) => folder.id !== deletedId)
        );
      }

      return { previousTree, previousList };
    },
    onError: (_err, _deletedId, context) => {
      // Rollback on error
      if (context?.previousTree) {
        queryClient.setQueryData(
          queryKeys.organization.folders.tree,
          context.previousTree
        );
      }
      if (context?.previousList) {
        queryClient.setQueryData(
          queryKeys.organization.folders.list(),
          context.previousList
        );
      }
    },
    onSettled: () => {
      // Always refetch after mutation settles
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.folders.all,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.folders.trash,
      });
    },
  });
}

/**
 * Restore a folder from trash
 *
 * Removes soft-delete flag and restores folder visibility.
 */
export function useRestoreFolderMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.folders[':id'].restore.$post({
        param: { id },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to restore folder'));
      }

      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.folders.all,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.folders.trash,
      });
    },
  });
}

/**
 * Move items to a folder
 *
 * Moves tables and/or linked tables to target folder.
 * Pass null as folder_id to move to root.
 */
export function useMoveItemsToFolderMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      folderId,
      tableIds,
      linkedTableIds,
    }: {
      folderId: string | null;
      tableIds?: string[];
      linkedTableIds?: string[];
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.organization.folders.move.$post({
        json: {
          folder_id: folderId,
          table_ids: tableIds,
          linked_table_ids: linkedTableIds,
        },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to move items'));
      }

      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.folders.all,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organization.items.all,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.tables.all,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.linkedTables.all,
      });
    },
  });
}
